// Виджет проверки «человек ли это» на форме входа.
//
// Рисуется ТОЛЬКО когда задан ключ сайта (VITE_TURNSTILE_SITE_KEY). Это не
// перестраховка, а условие безопасного выката: код уезжает на прод раньше
// ключей, и виджет, требующий несуществующий ключ, запер бы вход всем. Сервер
// устроен симметрично — пока нет секрета, проверка пропускается.
import { useEffect, useRef } from 'preact/hooks';

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (el: HTMLElement, opts: { sitekey: string; callback: (t: string) => void }) => string;
  remove: (id: string) => void;
};

export const turnstileEnabled = (): boolean => SITE_KEY.length > 0;

/** Загружаем скрипт один раз на страницу, а не на каждый показ формы. */
let loading: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if ((globalThis as { turnstile?: TurnstileApi }).turnstile) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_URL;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('turnstile_script_failed'));
    document.head.appendChild(el);
  });
  return loading;
}

export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let widgetId: string | null = null;
    let cancelled = false;

    void loadScript()
      .then(() => {
        const api = (globalThis as { turnstile?: TurnstileApi }).turnstile;
        if (cancelled || !api || !boxRef.current) return;
        widgetId = api.render(boxRef.current, { sitekey: SITE_KEY, callback: onToken });
      })
      .catch(() => {
        // Скрипт не загрузился (сеть, блокировщик). Молчим: сервер пропустит
        // запрос без токена, пока секрет не заведён, а когда заведён — вход
        // честно откажет, и человек увидит ошибку от сервера, а не пустой
        // экран без объяснений.
      });

    return () => {
      cancelled = true;
      const api = (globalThis as { turnstile?: TurnstileApi }).turnstile;
      if (widgetId && api) {
        try {
          api.remove(widgetId);
        } catch {
          /* виджет уже снят */
        }
      }
    };
  }, [onToken]);

  if (!SITE_KEY) return null;
  return <div ref={boxRef} class="turnstile-box" />;
}
